/**
 * Keeps `useFeatureFlagsStore` aligned with Better Auth (`SPEC_feature_flags_TK-12413.md`).
 *
 * Unlike `profile-auth-sync.client.ts`, this refetches on **both** transitions rather than
 * clearing on logout: a logged-out visitor still needs the global evaluation (guests can be shown
 * a feature), so "no session" is a real result to fetch, not an empty state.
 *
 * Native Capacitor additionally refetches on app foreground — see
 * `apps/capacitor/plugins/feature-flags-resume.client.ts`.
 */
export default defineNuxtPlugin(() => {
    const { isLoggedIn } = useBetterAuth()
    const featureFlagsStore = useFeatureFlagsStore()

    watch(
        isLoggedIn,
        async (loggedIn, wasLoggedIn) => {
            // First run (`immediate`) has `wasLoggedIn === undefined`: initial load, so a plain
            // load is enough. A real transition must bypass the cache — the same map is no longer
            // valid once identity changed, in either direction.
            if (wasLoggedIn === undefined) {
                await featureFlagsStore.ensureFlagsLoaded()
                return
            }
            if (loggedIn !== wasLoggedIn) {
                // `failClosed` because this is an identity change: a failed re-resolve must not
                // leave the previous identity's map in place. The Capacitor auth flows await the
                // same call while holding their screen lock — passing the same option here means
                // whichever of the two wins the race, the shared request behaves identically.
                await featureFlagsStore.refresh({ failClosed: true })
            }
        },
        { immediate: true }
    )
})
